import { useCallback } from "react";
import { Controller, useForm } from "react-hook-form";
import {
  CircularProgress,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";

import {
  BottomActionArea,
  BottomActionButton,
  FormHeader,
  NavigateBeforeButton,
  FormBody,
  FormContentContainer,
} from "../../../components";
import { FormContainer } from "../../../styles/styled";
import styled from "@emotion/styled";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { apiGateway } from "@contexts";
import { AxiosError } from "axios";
import { Question, QuestionsChallengeActionError } from "../../../interfaces";
import {
  ErrorType,
  onQuestionChallengeAxiosErrorInterpreter,
} from "../hooks/onQuestionChallengeError";
import { getQuestionsChallengeErrorDisplay } from "../constants/questionsChallengeErrorMessages";
import { useEnrolmentStatusContext } from "@contexts/EnrolmentStatusContext";
import {
  ENROLMENT_STEP,
  ENROLMENT_STEP_STATUS,
} from "@contexts/EnrolmentStatusContext";

interface Props {
  questions?: Question[];
  setQuestionsErrorNextStep: (step: ENROLMENT_STEP | null) => void;
  setQuestionsErrorNextStepStatus: (
    status: ENROLMENT_STEP_STATUS | null,
  ) => void;
  setShowBlockModal: (show: boolean) => void;
  setBlockModalContent: (content: {
    title: string;
    description?: string | React.ReactNode;
  }) => void;
  setShowRetryModal: (show: boolean) => void;
  setRetryModalContent: (content: {
    title: string;
    description?: string | React.ReactNode;
  }) => void;
}

interface Answer {
  questionId: string;
  answerId: string;
}

const PaddedRadioGroup = styled(RadioGroup)({
  paddingInline: "16px",
});

export const QuestionsChallengeForm: React.FC<Props> = ({
  questions,
  setQuestionsErrorNextStep,
  setQuestionsErrorNextStepStatus,
  setShowBlockModal,
  setBlockModalContent,
  setShowRetryModal,
  setRetryModalContent,
}) => {
  const { getEnrolmentStatus, setCurrentStep, setCurrentStepStatus } =
    useEnrolmentStatusContext();

  const {
    handleSubmit,
    control,
    formState: { isValid },
    reset,
  } = useForm({ mode: "onChange" });

  const { mutate: validateChallenge, isPending } = useMutation({
    mutationFn: (answers: Answer[]) =>
      apiGateway<void, ErrorType>(
        {
          method: "post",
          url: "/enrolment/questions-challenge/verify",
          data: { answers },
        },
        onQuestionChallengeAxiosErrorInterpreter,
      ),
    onSuccess: async () => {
      await getEnrolmentStatus();
    },
    onError: async (err) => {
      const handleActionError = async (
        error: QuestionsChallengeActionError,
      ) => {
        switch (error.action) {
          case "block":
            setBlockModalContent({
              title: error.name,
              description: error.description,
            });
            setShowBlockModal(true);
            if (error.nextStep && error.nextStepStatus) {
              setQuestionsErrorNextStep(error.nextStep);
              setQuestionsErrorNextStepStatus(error.nextStepStatus);
            }
            break;
          case "retry":
            setRetryModalContent({
              title: error.name,
              description: error.description,
            });
            setShowRetryModal(true);
            if (error.nextStep && error.nextStepStatus) {
              setCurrentStep(error.nextStep);
              setCurrentStepStatus(error.nextStepStatus);
            } else {
              await getEnrolmentStatus();
            }
            break;
          case "invalidInput":
            reset();
            if (error.nextStep === "QUESTIONS_CHALLENGE") {
              setRetryModalContent({
                title: error.name,
                description: error.description,
              });
              setShowRetryModal(true);
            }
            if (error.nextStep && error.nextStepStatus) {
              setCurrentStep(error.nextStep);
              setCurrentStepStatus(error.nextStepStatus);
            } else {
              await getEnrolmentStatus();
            }
            break;
        }
      };

      if (err instanceof QuestionsChallengeActionError) {
        await handleActionError(err);
        return;
      }
      // Fallback: e.g. invalid answers without action (legacy) or other errors
      const errorName =
        err instanceof AxiosError
          ? (err.response?.data as { error?: { name?: string } } | undefined)
              ?.error?.name
          : undefined;

      const frontErrorMessage = getQuestionsChallengeErrorDisplay(
        errorName ?? "unexpectedError",
      ).name;
      reset();
      console.error(frontErrorMessage);
      setRetryModalContent({
        title: "Error al generar preguntas",
        description: frontErrorMessage,
      });
      setShowRetryModal(true);
      const errorData = (err as AxiosError<ErrorType>).response?.data;
      const nextStep = errorData?.nextStep;
      const nextStepStatus = errorData?.nextStepStatus;
      if (nextStep && nextStepStatus) {
        setCurrentStep(nextStep);
        setCurrentStepStatus(nextStepStatus);
      } else {
        await getEnrolmentStatus();
      }
    },
  });
  const navigate = useNavigate();
  const handleBack = useCallback(() => {
    return navigate("/app/solicitudes");
  }, [navigate]);

  const onSubmit = async (data: Record<string, string>) => {
    const answers: Answer[] = Object.entries(data).map(([key, value]) => ({
      questionId: key,
      answerId: value,
    }));
    validateChallenge(answers);
  };
  return (
    <>
      <FormHeader
        title="Verificación de identidad"
        titleColor="white"
        left={<NavigateBeforeButton iconColor="white" onClick={handleBack} />}
      />
      <FormContentContainer disableGutters>
        <FormBody>
          <Typography>Responde estas preguntas de seguridad</Typography>
          <FormContainer onSubmit={handleSubmit(onSubmit)}>
            {questions?.map(({ questionId, questionText, options }) => (
              <div key={questionId}>
                <Typography fontWeight={500} marginBottom={"16px"}>
                  {questionId}. {questionText}
                </Typography>
                <Controller
                  control={control}
                  name={`${questionId}`}
                  defaultValue=""
                  rules={{ required: true }}
                  render={({ field }) => (
                    <PaddedRadioGroup {...field}>
                      {options.map(({ optionId, optionText }) => (
                        <FormControlLabel
                          key={`${questionId}-${optionId}`}
                          value={optionId}
                          control={<Radio size="small" />}
                          label={optionText}
                        />
                      ))}
                    </PaddedRadioGroup>
                  )}
                />
              </div>
            ))}
            <BottomActionArea>
              <BottomActionButton
                type="submit"
                disabled={!isValid || isPending}
              >
                {isPending ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  "Siguiente"
                )}
              </BottomActionButton>
            </BottomActionArea>
          </FormContainer>
        </FormBody>
      </FormContentContainer>
    </>
  );
};
